import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Compass, Home, Search } from "lucide-react";

/**
 * NotFound — friendly 404 surface for any route that doesn't match.
 * Replaces the previous catch-all Navigate-to-/ behavior so users see
 * an explicit "page not found" with clear next steps instead of being
 * silently redirected and wondering what just happened.
 */
export default function NotFound() {
  const loc = useLocation();
  return (
    <div data-testid="not-found-page" className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="text-tv-accent text-[10px] uppercase tracking-[0.4em] font-black mb-2">404 · Page not found</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter mb-3">
          We can't find that page.
        </h1>
        <p className="text-white/55 text-sm mb-1">
          The link <code className="text-tv-accent">{loc.pathname}</code> doesn't go anywhere on Tap In TV.
        </p>
        <p className="text-white/45 text-xs mb-8">It may have moved, or never existed in the first place.</p>

        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Link
            to="/"
            data-testid="not-found-home-btn"
            className="bg-tv-accent hover:bg-yellow-400 text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded-full inline-flex items-center justify-center gap-2"
          ><Home className="w-4 h-4" /> Back to home</Link>
          <Link
            to="/live"
            data-testid="not-found-live-btn"
            className="bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full inline-flex items-center justify-center gap-2 border border-white/10"
          ><Compass className="w-4 h-4" /> Watch what's live</Link>
          <Link
            to="/search"
            data-testid="not-found-search-btn"
            className="bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full inline-flex items-center justify-center gap-2 border border-white/10"
          ><Search className="w-4 h-4" /> Search</Link>
        </div>
      </div>
    </div>
  );
}
